import { HStack, Button, useBreakpointValue } from '@chakra-ui/react';
import { ReactElement } from 'react';
import { useMatch, Link, useLocation, matchPath } from 'react-router-dom';
import ContextMenu from '../../molecules/context-menu';

interface SubnavButtonProps {
  icon?: ReactElement;
  label: string;
  action: string | (() => void);
  match?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  dataTestId?: string;
}

export interface SubnavButtonGroupProps {
  buttonGroups: SubnavButtonProps[][];
}

const subnavButtonStyleProps = {
  variant: 'unstyled',
  display: 'flex',
  alignItems: 'center',
  height: '24px',
  px: 2,
  py: 1,
  borderRadius: 'md',
  backgroundColor: 'gray.50',
  fontWeight: 'normal',
  fontSize: 'xs',
  lineHeight: '16px',
  _hover: { backgroundColor: 'green.300' },
};

export default function SubnavButtonGroups({
  buttonGroups,
}: SubnavButtonGroupProps) {
  const location = useLocation();
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  if (!isSmallScreen) {
    return buttonGroups.map((buttons, index) => (
      <HStack
        // eslint-disable-next-line react/no-array-index-key
        key={`${index}_${buttons.length}`}
        spacing={0}
        backgroundColor="gray.50"
        borderRadius="md"
      >
        {buttons.map((button) => (
          <SubnavButton key={button.label} {...button} />
        ))}
      </HStack>
    ));
  }
  const activeButton = buttonGroups
    .flat()
    .find(
      (button) =>
        button.match &&
        typeof button.action === 'string' &&
        matchPath(button.match, location.pathname)
    );
  return (
    <ContextMenu
      items={buttonGroups.map((buttons, index) => ({
        // eslint-disable-next-line react/no-array-index-key
        key: `${index}_${buttons.length}`,
        members: buttons.map((button) => ({
          key: button.label,
          label: button.label,
          action: button.isDisabled ? undefined : button.action,
          isDisabled: button.isDisabled,
        })),
      }))}
      triggerLabel={activeButton?.label}
      triggerButtonProps={subnavButtonStyleProps}
    />
  );
}

export function SubnavButton({
  icon,
  label,
  action,
  match,
  isLoading,
  isDisabled,
  dataTestId,
}: SubnavButtonProps) {
  const currentPathMatch = useMatch(match ?? '');
  const buttonProps =
    typeof action === 'string' ? { as: Link, to: action } : { onClick: action };

  return (
    <Button
      {...subnavButtonStyleProps}
      backgroundColor={currentPathMatch ? 'green.300' : 'gray.50'}
      fontWeight={currentPathMatch ? 'bold' : 'normal'}
      _hover={{ backgroundColor: 'green.300' }}
      rightIcon={icon}
      isLoading={isLoading}
      isDisabled={isDisabled}
      data-testid={dataTestId}
      {...buttonProps}
    >
      {label}
    </Button>
  );
}
